import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import { ProgrammeCard } from "../components/blocks/Cards";
import { PROGRAMMES, PROGRAMME_CATEGORIES, FILTER_OPTIONS } from "../data/programmes";

const FILTERS = [
  { key: "capability", label: "Capability" },
  { key: "audience", label: "Audience" },
  { key: "format", label: "Format" },
  { key: "duration", label: "Duration" },
];

export default function ProgrammesPage() {
  const [filters, setFilters] = useState({ capability: "", audience: "", format: "", duration: "" });
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  const results = useMemo(
    () =>
      PROGRAMMES.filter((p) =>
        FILTERS.every(({ key }) => !filters[key] || p[key] === filters[key])
      ),
    [filters]
  );

  return (
    <>
      <SEO
        title="Programmes — Executive Education Catalogue | The Vantage Institute™"
        description="Explore Vantage masterclasses, executive programmes, certifications, corporate programmes, workshops, micro-learning and learning journeys — by capability, audience, format and duration."
        path="/programmes"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Programmes" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Programmes</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            The Vantage Programme Catalogue.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            From single-day workshops to structured learning journeys — every programme is built on the Vantage
            Thinking Toolbox™ and designed for application. Full curricula, schedules and enrolment details are
            being finalised; register your interest to be notified as each programme opens.
          </p>
        </div>
      </section>

      <section className="border-b border-platinum bg-paper" data-testid="programme-categories">
        <div className="container-x py-10">
          <p className="eyebrow">Catalogue Structure</p>
          <ul className="mt-4 flex flex-wrap gap-2.5">
            {PROGRAMME_CATEGORIES.map((c) => (
              <li key={c} className="border border-platinum bg-white px-3.5 py-2 font-sub text-xs font-semibold uppercase tracking-wider text-navy">
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="container-x py-14 sm:py-20" data-testid="programme-catalogue">
        <SectionHeading
          eyebrow="Find a Programme"
          title="Filter by what matters to you."
        />
        <form className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Programme filters" data-testid="programme-filters">
          {FILTERS.map(({ key, label }) => (
            <div key={key}>
              <label htmlFor={`filter-${key}`} className="input-label">{label}</label>
              <select
                id={`filter-${key}`}
                className="input-field"
                value={filters[key]}
                onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                data-testid={`filter-${key}`}
              >
                <option value="">All</option>
                {FILTER_OPTIONS[key].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          ))}
        </form>
        <p className="mt-6 font-sub text-xs font-semibold uppercase tracking-[0.14em] text-slate" role="status" data-testid="filter-results-count">
          {results.length} programme{results.length === 1 ? "" : "s"} shown
        </p>
        {results.length > 0 ? (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((p) => (
              <div key={p.slug} id={p.slug} className="scroll-mt-28">
                <ProgrammeCard programme={p} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 card" data-testid="filter-no-results">
            <p className="text-sm text-slate">
              No programmes match this combination yet. The catalogue is growing —{" "}
              <a href="/contact" className="text-link">talk to Vantage</a> about what you need.
            </p>
          </div>
        )}
      </section>

      <CTASection
        title="Not sure which programme fits?"
        text="Start from your learning path — or speak with the Institute and we will point you to the right starting point."
        primaryLabel="Find Your Learning Path"
        primaryTo="/learning"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="programmes-cta"
      />
    </>
  );
}
